import { useContext, useEffect, useState, ChangeEvent } from 'react';
import {
  Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, FormGroup, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow,
  Typography,
} from '@mui/material';
import { ScheduleCreateContext, ScheduleCreateValues } from '../../contexts/ScheduleCreateContext';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import useLogError from '../../../../hooks/useLogError';
import { CountZoneDto } from '../../../../app/services/api/generated';
import { CloseDialogAction } from '../../../../components/shared/dialogs/CloseDialogAction';

const ScheduleCreateZonesModal = () => {
  const {
    t,
    isCountZonesModalOpen,
    countZonesData,
    handleClose,
    schedules,
    handleCountZones,
  } = useContext(ScheduleCreateContext) as ScheduleCreateValues;

  const { getAvailableCountZones } = useNswagClient();
  const { logError } = useLogError();

  const [dataFromApi, setDataFromApi] = useState<CountZoneDto[]>([]);
  const [tempZoneData, setTempZoneData] = useState<CountZoneDto[]>([]);

  useEffect(() => {
    if (isCountZonesModalOpen) {
      setTempZoneData(countZonesData.map(z => ({
        id: z.locationId,
        name: z.name,
      })));
      getZones();
    }
  }, [isCountZonesModalOpen]);

  const getZones = () => {
    const storeNumbers = schedules
      .flatMap(s => s.storeNumbers)
      .filter((v, i, arr): v is string => typeof v === 'string' && arr.indexOf(v) === i);

    if (storeNumbers.length === 0) {
      setDataFromApi([]);
      return;
    }

    getAvailableCountZones(storeNumbers)
      .then(result => {
        const zones = result.countZones ?? [];
        setDataFromApi(zones);
      })
      .catch(logError);
  };

  const addZoneToTempList = (zone: CountZoneDto) => {
    setTempZoneData(prev => {
      const exists = prev?.some(z => z.id === zone.id);
      return exists ? prev : [...(prev ?? []), zone];
    });
  };

  const handleZoneCheckboxChange = (zone: CountZoneDto, isChecked: boolean) => {
    if (isChecked) {
      const exists = tempZoneData?.some(z => z.id === zone.id);
      if (!exists) {
        addZoneToTempList(zone);
      }
    } else {
      setTempZoneData(prev => prev?.filter(z => z.id !== zone.id) ?? []);
    }
  };

  const handleSelectAllChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setTempZoneData(checked ? [...dataFromApi] : []);
  };

  const handleConfirm = () => {
    handleCountZones(tempZoneData || []);
    setTempZoneData([]);
  };

  const handleCancel = () => {
    setTempZoneData([]);
    handleClose();
  };

  return (
    <Dialog open={isCountZonesModalOpen}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth>
      <DialogTitle>
        {t('countZones.title')}
        <CloseDialogAction onCancel={handleCancel} />
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontStyle: 'italic', mb: 5 }}>{t('countZones.info')}</Typography>
        <TableContainer sx={{ overflowY: 'auto', border: '1px solid' }}
          style={{ maxHeight: '400px' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('countZones.name')}</TableCell>
                <TableCell>{t('countZones.numberOfItems')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataFromApi.map((zone) => (
                <TableRow key={zone.id}>
                  <TableCell style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      color="primary"
                      checked={
                        tempZoneData.some(z => z.id === zone.id) || countZonesData?.some(z => z.locationId === zone.id)}
                      onChange={(e) => handleZoneCheckboxChange(zone, e.target.checked)}
                      inputProps={{ 'aria-label': `select zone ${zone.name}` }}
                    />
                    {zone.name}
                  </TableCell>
                  <TableCell>{zone.numberOfItems}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <FormGroup sx={{ pt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                onChange={handleSelectAllChange}
              />
            }
            label={t('countZones.selectAll')}
          />
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button variant="secondary"
          size="lg"
          onClick={handleCancel}>
          {t('cancel')}
        </Button>
        <Button size="lg"
          onClick={handleConfirm}>
          {t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleCreateZonesModal;
